import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AnnounceVersionResponse,
  BroadcastInput,
  BroadcastResponse,
  NotificationDto,
  NotificationListResponse,
  PreviewRecipientsInput,
  PreviewRecipientsResponse,
  UnreadCountResponse,
} from '@forest/contracts';
import { api } from '@/lib/api-client';

export type NotificationFilter = 'all' | 'unread';

/**
 * Poll-backed unread-count badge for the bell (NFR-NOTIF-003). 45 s interval
 * plus a refetch on window focus so the badge feels live without a websocket.
 */
export function useUnreadCount(enabled = true) {
  return useQuery<UnreadCountResponse, Error, number>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () =>
      api.get('notifications/unread-count').json<UnreadCountResponse>(),
    select: (d) => d.count,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
    enabled,
  });
}

/**
 * Cursor-paginated notification list for the bell dropdown and the full
 * page. `filter` selects all vs unread. `nextCursor` drives load-more.
 */
export function useNotifications(filter: NotificationFilter = 'all', limit = 20) {
  return useInfiniteQuery<NotificationListResponse>({
    queryKey: ['notifications', 'list', filter, limit],
    queryFn: async ({ pageParam }) => {
      const sp = new URLSearchParams();
      sp.set('limit', String(limit));
      sp.set('filter', filter);
      if (typeof pageParam === 'string' && pageParam) sp.set('cursor', pageParam);
      return api
        .get('notifications', { searchParams: sp })
        .json<NotificationListResponse>();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation<NotificationDto, Error, string>({
    mutationFn: async (id) =>
      api.post(`notifications/${id}/read`).json<NotificationDto>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, Error, void>({
    mutationFn: async () =>
      api.post('notifications/read-all').json<{ updated: number }>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// ----------------------------------------------------------------
// Admin (SUPER_ADMIN) — broadcast composer
// ----------------------------------------------------------------

export function useBroadcast() {
  const qc = useQueryClient();
  return useMutation<BroadcastResponse, Error, BroadcastInput>({
    mutationFn: async (input) =>
      api
        .post('admin/notifications/broadcast', { json: input })
        .json<BroadcastResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function usePreviewRecipients() {
  return useMutation<PreviewRecipientsResponse, Error, PreviewRecipientsInput>({
    mutationFn: async (input) =>
      api
        .post('admin/notifications/preview-recipients', { json: input })
        .json<PreviewRecipientsResponse>(),
  });
}

export function useAnnounceWebVersion() {
  const qc = useQueryClient();
  return useMutation<AnnounceVersionResponse, Error, void>({
    mutationFn: async () =>
      api
        .post('admin/notifications/announce-web-version')
        .json<AnnounceVersionResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useAnnounceMobileVersion() {
  const qc = useQueryClient();
  return useMutation<AnnounceVersionResponse, Error, void>({
    mutationFn: async () =>
      api
        .post('admin/notifications/announce-mobile-version')
        .json<AnnounceVersionResponse>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
